import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Save, X, Building2, User, MapPin, Phone, Mail, Globe,
    FileText, ChevronDown, ChevronUp, Plus, Trash2, Check,
    Shield, CreditCard, Truck, Ship, Plane, Package, Users,
    RefreshCw, Edit2, AlertCircle, CheckCircle2, Link2,
    Pencil
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { toast } from 'sonner';

import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { select } from '../ui/select';
import { SearchBar } from '../SearchBox/SearchBar';
import { Select } from '@radix-ui/react-select';
import { getAddressByID, DeleteCompanyAddress, createAddressBook, UpdateAddressBook, createAddressBookAddress, UpdateAddressBookAddress } from '@/redux/Address/actions';
import { validateForm } from "../ui/useFormValidation";
import DateInputwithcal from '../ui/DateInputwithcal';
import { set } from 'date-fns';
import moment from 'moment';

const AddCompany = ({ onBack, bookingData = null }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('company');
    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList, commodityGroupList, containerTypeList, operatorList } = useSelector((state) => state.common);

    const [formData, setFormData] = useState();
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(null);

    const [expandedSections, setExpandedSections] = useState({
        entityType: true,
        customerDetails: true,
        addressDetails: true,
        linkEntities: false
    });

    useEffect(() => {
        if (bookingData != 0 && bookingData != null) {
            _getAddressBook();
        }
        else {
            _setNewAddressBook();
        }
    }, [bookingData]);

    const _getAddressBook = async () => {
        const resultData = await dispatch(getAddressByID(bookingData));
        setFormData(resultData);
        setAddresses(resultData?.companyAddress || []);
    }

    const _setNewAddressBook = async () => {
        setFormData();
        setAddresses([]);
    }

    const entityTypeOptions = [
        { id: 'exporter', label: 'Exporter', icon: Package },
        { id: 'vendor', label: 'Vendor', icon: Building2 },
        { id: 'importer', label: 'Importer', icon: Globe },
        { id: 'nvocc', label: 'NVOCC', icon: Ship },
        { id: 'billToParty', label: 'Bill to Party', icon: CreditCard },
        { id: 'pickup', label: 'Pickup', icon: Truck },
        { id: 'emptyreturn', label: 'Empty Return', icon: Truck },
        { id: 'forwarder', label: 'Forwarder', icon: Ship },
        { id: 'agent', label: 'Agent', icon: User },
        { id: 'warehouse', label: 'Warehouse', icon: Package },
        { id: 'notify', label: 'Notify', icon: Mail },
        { id: 'trucker', label: 'Trucker', icon: Truck },
        { id: 'custombroker', label: 'Custom Broker', icon: Shield },
        { id: 'consignee', label: 'Consignee', icon: Users },
        { id: 'oceaN_CARRIER', label: 'Ocean Carrier', icon: Ship },
        { id: 'broker', label: 'Broker', icon: Users },
        { id: 'other', label: 'Other', icon: FileText }
    ];

    const tabs = [
        { id: 'company', label: 'Company Info', icon: Building2 },
        { id: 'docs', label: 'Docs/Special Notes', icon: FileText },
        { id: 'compliance', label: 'Compliance/Certification', icon: Shield },
        { id: 'accounting', label: 'Accounting', icon: CreditCard },
        { id: 'trucker', label: 'Trucker Services', icon: Truck },
        { id: 'personal', label: 'Personal Information', icon: User },
        { id: 'crm', label: 'CRM Details', icon: Users }
    ];

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleEntityTypeToggle = (typeId) => {
        var changeAddress = [...addresses];
        const index = selectedAddressIndex;
        if (changeAddress[index]) {
            changeAddress[index][typeId] = !changeAddress[index][typeId];
        }
        setAddresses(changeAddress);
    };

    const handleSave = () => {
        console.log('Saving company...', formData);
        navigate('/address-book');
    };

    const addAddress = () => {
        if (formData?.address1 && formData?.city && formData?.country) {
            setAddresses(prev => [...prev, {
                id: Date.now(),
                companyName: formData?.addressCompanyName || formData?.companyName,
                officeType: formData?.addressOfficeType,
                address1: formData?.address1,
                city: formData?.city,
                country: formData?.country,
                categories: formData?.entityTypes.join(', ') || 'General'
            }]);
        }
    };

    const SectionHeader = ({ icon: Icon, title, color, section, badge }) => (
        <button
            onClick={() => toggleSection(section)}
            className={`w-full flex items-center justify-between p-4 ${color} rounded-t-xl transition-all hover:opacity-90`}
        >
            <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-base font-semibold text-white">{title}</h2>
                {badge && (
                    <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-medium rounded-full">
                        {badge}
                    </span>
                )}
            </div>
            {expandedSections[section] ? (
                <ChevronUp className="w-5 h-5 text-white/80" />
            ) : (
                <ChevronDown className="w-5 h-5 text-white/80" />
            )}
        </button>
    );

    const InputField = ({ label, required, children, className = '' }) => (
        <div className={className}>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {children}
        </div>
    );

    const inputClass = "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all";
    const selectClass = "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all appearance-none cursor-pointer";

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 space-y-6 pb-12"
        >
            {/* Header */}
            <div className="bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 rounded-2xl shadow-xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                </div>

                <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                            <Building2 className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 text-teal-100 text-sm mb-1">
                                <span>Address Book</span>
                                <span>/</span>
                                <span>Add New Company</span>
                            </div>
                            <h1 className="text-2xl font-bold text-white">Company Details</h1>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-all shadow-lg hover:shadow-xl flex items-center gap-2 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            Check DPL
                        </button>
                        <button className="px-5 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium transition-all shadow-lg hover:shadow-xl flex items-center gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            Check Duplicate
                        </button>
                        <button onClick={handleSave} className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all shadow-lg hover:shadow-xl flex items-center gap-2 text-sm">
                            <Save className="w-4 h-4" />
                            Save
                        </button>
                        <button onClick={() => onBack()} className="px-5 py-2.5 bg-red-500/80 hover:bg-red-500 text-white rounded-xl font-medium transition-all flex items-center gap-2 text-sm">
                            <X className="w-4 h-4" /> Cancel
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-2">
                <div className="flex flex-wrap gap-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${activeTab === tab.id
                                ? 'bg-teal-600 text-white shadow-md'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === 'company' && (
                <>
                    {/* Entity Type */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <SectionHeader icon={Users} title="Entity Type" color="bg-indigo-600" section="entityType" badge={`${formData?.entityTypes?.length} selected`} />

                        {expandedSections.entityType && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className="p-5"
                            >
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                    {entityTypeOptions.map((type) => (
                                        <label
                                            key={type.id}
                                            className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${addresses[selectedAddressIndex]?.[type.id]
                                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                                                : 'border-slate-200 dark:border-slate-700 hover:border-teal-300'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={addresses[selectedAddressIndex]?.[type.id]}
                                                onChange={() => handleEntityTypeToggle(type.id)}
                                                className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${addresses[selectedAddressIndex]?.[type.id]
                                                ? 'bg-teal-500 border-teal-500'
                                                : 'border-slate-300 dark:border-slate-600'
                                                }`}>
                                                {addresses[selectedAddressIndex]?.[type.id] && <Check className="w-3 h-3 text-white" />}
                                            </div>
                                            <type.icon className="w-4 h-4 text-slate-500" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{type.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Customer Details */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <SectionHeader icon={Building2} title="Customer Details" color="bg-blue-600" section="customerDetails" />

                        {expandedSections.customerDetails && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className="p-5"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    <InputField label="Complexity Level">
                                        <select name="complexityLevel" value={formData?.complexityLevel} onChange={handleInputChange} className={selectClass}>
                                            <option value="-NA-">-NA-</option>
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Company Code">
                                        <input type="text" name="companyCode" value={formData?.companyCode} onChange={handleInputChange} placeholder="e.g., 2163" className={inputClass} />
                                    </InputField>
                                    <InputField label="Office Type">
                                        <select name="officeType" value={formData?.officeType} onChange={handleInputChange} className={selectClass}>
                                            <option value="WISCONSIN">WISCONSIN</option>
                                            <option value="NEW YORK">NEW YORK</option>
                                            <option value="CALIFORNIA">CALIFORNIA</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Company Name" required>
                                        <input type="text" name="companyName" value={formData?.companyName} onChange={handleInputChange} placeholder="Company Name" className={inputClass} />
                                    </InputField>
                                    <InputField label="EDI Code" required>
                                        <input type="text" name="ediCode" value={formData?.ediCode} onChange={handleInputChange} placeholder="e.g., 1_28_VDR" className={inputClass} />
                                    </InputField>
                                    <InputField label="FMC #">
                                        <input type="text" name="fmcNumber" value={formData?.fmcNumber} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="USPPI Contact Name">
                                        <input type="text" name="usppiContactName" value={formData?.usppiContactName} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="USPPI Phone Number">
                                        <input type="text" name="usppiPhone" value={formData?.usppiPhone} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="USPPI Email ID">
                                        <input type="email" name="usppiEmail" value={formData?.usppiEmail} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Select Sales Person" required>
                                        <select name="salesPerson" value={formData?.salesPerson} onChange={handleInputChange} className={selectClass}>
                                            <option value="">SELECT SALES PERSON</option>
                                            <option value="kh">Krenz and Hannan Intl</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Carrier Contract #">
                                        <input type="text" name="carrierContract" value={formData?.carrierContract} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Coordinator Name">
                                        <input type="text" name="coordinatorName" value={formData?.coordinatorName} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Coordinator Email">
                                        <input type="email" name="coordinatorEmail" value={formData?.coordinatorEmail} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Coordinator Phone">
                                        <input type="text" name="coordinatorPhone" value={formData?.coordinatorPhone} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Identity Type">
                                        <select name="identityType" value={formData?.identityType} onChange={handleInputChange} className={selectClass}>
                                            <option value="">SELECT ID TYPE</option>
                                            <option value="ein">EIN</option>
                                            <option value="ssn">SSN</option>
                                            <option value="passport">Passport</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Identity No">
                                        <input type="text" name="identityNo" value={formData?.identityNo} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Importer Tax ID">
                                        <input type="text" name="importerTaxId" value={formData?.importerTaxId} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="EORI Number">
                                        <input type="text" name="eoriNumber" value={formData?.eoriNumber} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Scac/IATA Code">
                                        <input type="text" name="scacIataCode" value={formData?.scacIataCode} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Insurance Valid Date">
                                        <input type="date" name="insuranceValidDate" value={formData?.insuranceValidDate} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Insurance Expire Date">
                                        <input type="date" name="insuranceExpireDate" value={formData?.insuranceExpireDate} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Profit Margin Type">
                                        <select name="profitMarginType" value={formData?.profitMarginType} onChange={handleInputChange} className={selectClass}>
                                            <option value="lumpSum">Lump Sum</option>
                                            <option value="percentage">Percentage</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Consignee Type">
                                        <select name="consigneeType" value={formData?.consigneeType} onChange={handleInputChange} className={selectClass}>
                                            <option value="">SELECT</option>
                                            <option value="direct">Direct</option>
                                            <option value="indirect">Indirect</option>
                                        </select>
                                    </InputField>
                                    <div className="flex items-center gap-6 col-span-1">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" name="isDplActive" checked={formData?.isDplActive} onChange={handleInputChange} className="w-5 h-5 rounded text-teal-600" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Is DPL Active</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" name="overrideDpl" checked={formData?.overrideDpl} onChange={handleInputChange} className="w-5 h-5 rounded text-teal-600" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Override DPL</span>
                                        </label>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Address Details */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <SectionHeader icon={MapPin} title="Address Details" color="bg-emerald-600" section="addressDetails" />

                        {expandedSections.addressDetails && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className="p-5"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    <InputField label="Company Name" required>
                                        <input type="text" name="addressCompanyName" value={formData?.addressCompanyName} onChange={handleInputChange} placeholder="Company Name" className={inputClass} />
                                    </InputField>
                                    <InputField label="Parent Company">
                                        <input type="text" name="parentCompany" value={formData?.parentCompany} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Office Type">
                                        <select name="addressOfficeType" value={formData?.addressOfficeType} onChange={handleInputChange} className={selectClass}>
                                            <option value="Main Office">Main Office</option>
                                            <option value="Sub-Office">Sub-Office</option>
                                            <option value="Branch">Branch</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Address 1" required>
                                        <input type="text" name="address1" value={formData?.address1} onChange={handleInputChange} placeholder="Street Address" className={inputClass} />
                                    </InputField>
                                    <InputField label="Address 2">
                                        <input type="text" name="address2" value={formData?.address2} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Address 3">
                                        <input type="text" name="address3" value={formData?.address3} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Firms Code">
                                        <input type="text" name="firmsCode" value={formData?.firmsCode} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Country" required>
                                        <select name="country" value={formData?.country} onChange={handleInputChange} className={selectClass}>
                                            <option value="USA">USA</option>
                                            <option value="Canada">Canada</option>
                                            <option value="Mexico">Mexico</option>
                                            <option value="UK">UK</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Postal Code" required>
                                        <input type="text" name="postalCode" value={formData?.postalCode} onChange={handleInputChange} placeholder="e.g., 98134" className={inputClass} />
                                    </InputField>
                                    <InputField label="City" required>
                                        <input type="text" name="city" value={formData?.city} onChange={handleInputChange} placeholder="e.g., SEATTLE" className={inputClass} />
                                    </InputField>
                                    <InputField label="State" required>
                                        <input type="text" name="state" value={formData?.state} onChange={handleInputChange} placeholder="e.g., WA" className={inputClass} />
                                    </InputField>
                                    <InputField label="Province">
                                        <input type="text" name="province" value={formData?.province} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Contact Person">
                                        <input type="text" name="contactPerson" value={formData?.contactPerson} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Contact Person Position">
                                        <select name="contactPosition" value={formData?.contactPosition} onChange={handleInputChange} className={selectClass}>
                                            <option value="">SELECT POSITION</option>
                                            <option value="manager">Manager</option>
                                            <option value="director">Director</option>
                                            <option value="executive">Executive</option>
                                        </select>
                                    </InputField>
                                    <InputField label="Email">
                                        <input type="email" name="email" value={formData?.email} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Office Tel #">
                                        <input type="text" name="officeTel" value={formData?.officeTel} onChange={handleInputChange} placeholder="e.g., 206-805-0220" className={inputClass} />
                                    </InputField>
                                    <InputField label="Customer SAP A/C #">
                                        <input type="text" name="customerSapAc" value={formData?.customerSapAc} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Vendor SAP A/C #">
                                        <input type="text" name="vendorSapAc" value={formData?.vendorSapAc} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Fax #">
                                        <input type="text" name="fax" value={formData?.fax} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Tel # 2">
                                        <input type="text" name="tel2" value={formData?.tel2} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Toll Free #">
                                        <input type="text" name="tollFree" value={formData?.tollFree} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Alternative Email">
                                        <input type="email" name="alternativeEmail" value={formData?.alternativeEmail} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Accounting Email">
                                        <input type="email" name="accountingEmail" value={formData?.accountingEmail} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <InputField label="Notification Email" className="col-span-1 md:col-span-2">
                                        <input type="text" name="notificationEmail" value={formData?.notificationEmail} onChange={handleInputChange} placeholder="Use comma (,) to separate multiple emails" className={inputClass} />
                                        <p className="text-xs text-slate-500 mt-1">* You can add multiple email using comma (,) separated.</p>
                                    </InputField>
                                    <InputField label="Vendor's Tax ID">
                                        <input type="text" name="vendorTaxId" value={formData?.vendorTaxId} onChange={handleInputChange} className={inputClass} />
                                    </InputField>
                                    <div className="flex items-center gap-6 col-span-1 md:col-span-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" name="doNotSendNotification" checked={formData?.doNotSendNotification} onChange={handleInputChange} className="w-5 h-5 rounded text-teal-600" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Do Not Send Notification</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" name="vendorEligible1099" checked={formData?.vendorEligible1099} onChange={handleInputChange} className="w-5 h-5 rounded text-teal-600" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Vendor Eligible for 1099</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button onClick={addAddress} className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-all flex items-center gap-2 text-sm">
                                        <Plus className="w-4 h-4" />
                                        Add New Address
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Link to Entities */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <SectionHeader icon={Link2} title="Link to Entities" color="bg-purple-600" section="linkEntities" />

                        {expandedSections.linkEntities && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className="p-5"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[
                                        { name: 'linkedShipper', label: 'Shipper', icon: Package },
                                        { name: 'linkedTrucker', label: 'Trucker', icon: Truck },
                                        { name: 'linkedConsignee', label: 'Consignee', icon: Users },
                                        { name: 'linkedCarrier', label: 'Carrier', icon: Ship }
                                    ].map((entity) => (
                                        <div key={entity.name} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                            <div className="flex items-center gap-2 mb-3">
                                                <entity.icon className="w-5 h-5 text-purple-600" />
                                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{entity.label}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <select name={entity.name} value={formData[entity.name]} onChange={handleInputChange} className={`${selectClass} flex-1`}>
                                                    <option value="ALL">ALL</option>
                                                    <option value="specific">Specific</option>
                                                </select>
                                                <button className="p-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                                <button className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Address List */}
                    {addresses.length > 0 && (
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="bg-slate-700 p-4">
                                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                                    <MapPin className="w-5 h-5" />
                                    Address List
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-slate-100 dark:bg-slate-800">
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Address Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Email</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Address1</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">City</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Country</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Type</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Created AT</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {addresses.map((addr, key) => (
                                            <tr key={addr.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{addr?.addressname}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{addr?.email}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{addr?.addresS1}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{addr?.adD_CITY}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{addr?.country}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{addr?.typetext}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{addr?.createdon ? moment(addr?.createdon).format('DD MMM YYYY') : ''}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <button className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                                        onClick={() => { setSelectedAddressIndex(key); }}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Other tabs placeholder */}
            {activeTab !== 'company' && (
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{tabs.find(t => t.id === activeTab)?.label}</h3>
                    <p className="text-slate-500 dark:text-slate-400">This section is under development</p>
                </div>
            )}       

            {/* Sticky Bottom Actions */}
            <div className="sticky bottom-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg rounded-xl shadow-lg border border-slate-200 dark:border-slate-800 p-4">
                <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                        <span className="text-red-500">*</span> Required fields must be filled
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => onBack()} className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-medium transition-all flex items-center gap-2">
                            <X className="w-4 h-4" /> Cancel
                        </button>
                        <button onClick={handleSave} className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-all shadow-lg hover:shadow-xl flex items-center gap-2">
                            <Save className="w-4 h-4" /> Save Company
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default AddCompany;